// ./src/utils/api/LostFoundItemService.ts
//
// ROADMAP #68 — lost & found: things found at an event. Logged by the crew (held at once), or REPORTED by a finder on the
// event site (`reported`) and confirmed at the desk. OpResult-native, event-scoped; owner or crew with lostfound.manage.
//
//   list / get / create / update / remove — the Lost & found page's Items tab; `total` on the list result.
//   move — receive at the desk, hand back (who, how it was checked), dispose, mark never received — and undo (→ held).
//   addPhoto / removePhoto — photos, re-encoded on the server (EXIF gone); crew-only.
//   byCode — what the desk's scan of a finder's QR / a bag label opens.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// Mirrors eventsmagic-api boundary/lostFound.ts.
export const LOST_FOUND_CATEGORIES = [
  "phone",
  "wallet",
  "keys",
  "bag",
  "clothing",
  "glasses",
  "jewellery",
  "id_card",
  "bottle",
  "child_item",
  "electronics",
  "other",
] as const;

export type LostFoundCategory = (typeof LOST_FOUND_CATEGORIES)[number];

/** Valuable by default: category only in public, an ID check at handover. */
export const LOST_FOUND_VALUABLE_CATEGORIES: string[] = ["phone", "wallet", "id_card", "jewellery", "electronics"];

export const LOST_FOUND_ITEM_STATE = {
  REPORTED: "reported",
  HELD: "held",
  RETURNED: "returned",
  DISPOSED: "disposed",
  NOT_RECEIVED: "not_received",
} as const;

export type LostFoundItemState = (typeof LOST_FOUND_ITEM_STATE)[keyof typeof LOST_FOUND_ITEM_STATE];

/** The moves an item may make — the server's own table (boundary/lostFound.ts LOST_FOUND_ITEM_MOVES). */
export const LOST_FOUND_ITEM_MOVES: Record<string, string[]> = {
  reported: ["held", "not_received"],
  held: ["returned", "disposed"],
  returned: ["held"],
  disposed: ["held"],
  not_received: ["reported", "held"],
};

export const LOST_FOUND_VERIFICATION_KINDS = ["described_detail", "id_checked", "qr_tag", "known_guest", "other"] as const;
/** A valuable goes back only after one of these (the server refuses the rest). */
export const LOST_FOUND_VALUABLE_CHECKS: string[] = ["id_checked", "qr_tag"];
export const LOST_FOUND_DISPOSAL_KINDS = ["discarded", "donated", "to_police", "to_venue"] as const;
export const LOST_FOUND_AUDIENCES = ["off", "guests", "everyone"] as const;

export const LOST_FOUND_MAX = {
  PUBLIC_DESCRIPTION: 512,
  DESCRIPTION: 4096,
  WHERE: 512,
  LABEL: 256,
  STORED_WHERE: 256,
  VERIFICATION_NOTE: 512,
  DESK_NOTE: 512,
  PHOTOS: 8,
} as const;

/** `event.settings.lostFound` — mirrors the server's reader and defaults (owner, 2026-10-22). */
export interface LostFoundSettings {
  reportLost: string;
  reportFound: string;
  showList: string;
  deskServicePointId: string | null;
  deskNote: string | null;
  keepDays: number;
}

export const LOST_FOUND_SETTINGS_DEFAULT: LostFoundSettings = {
  reportLost: "guests",
  reportFound: "guests",
  showList: "off",
  deskServicePointId: null,
  deskNote: null,
  keepDays: 30,
};

const audienceOr = (v: unknown, fallback: string): string => ((LOST_FOUND_AUDIENCES as readonly string[]).includes(String(v)) ? String(v) : fallback);

/** An event's lost & found settings, every key defaulted (the server reads them the same way). */
export const lostFoundSettingsOf = (settings: { lostFound?: any } | null | undefined): LostFoundSettings => {
  const raw = settings?.lostFound ?? {};
  const d = LOST_FOUND_SETTINGS_DEFAULT;
  const keep = Math.round(Number(raw.keepDays));

  return {
    reportLost: audienceOr(raw.reportLost, d.reportLost),
    reportFound: audienceOr(raw.reportFound, d.reportFound),
    showList: audienceOr(raw.showList, d.showList),
    deskServicePointId: typeof raw.deskServicePointId === "string" && raw.deskServicePointId ? raw.deskServicePointId : null,
    deskNote: typeof raw.deskNote === "string" && raw.deskNote.trim() ? raw.deskNote : null,
    keepDays: Number.isFinite(keep) ? Math.min(365, Math.max(1, keep)) : d.keepDays,
  };
};

const PERSON_FIELDS = `kind id name email phone removed`;

const ITEM_FIELDS = `
  id
  accountId
  eventId
  eventTimeSlotId
  sequenceNo
  code
  source
  isTest
  category
  isValuable
  publicDescription
  description
  foundAt
  foundWhere
  latitude
  longitude
  locationAccuracyM
  foundServicePointId
  foundMapElementId
  finder { ${PERSON_FIELDS} }
  foundByLabel
  itemState
  receivedAt
  receivedByLabel
  heldServicePointId
  storedWhere
  ownerGuestId
  owner { ${PERSON_FIELDS} }
  ownerQrTagId
  keepUntil
  returnedAt
  returnedByLabel
  returnedTo { ${PERSON_FIELDS} }
  returnedToReportId
  returnedServicePointId
  verificationKind
  verificationNote
  disposalKind
  disposedAt
  disposedByLabel
  photos { id uploadedFileId uploadedFile { id fileUrl thumbnailUrl } addedBy caption }
  rights
  createdAt
  updatedAt
`;

const RESULT = (name: string) => `
    ${name} {
      code
      data { ${ITEM_FIELDS} }
      errors { name errors warnings }
    }`;

const LIST_QUERY = gql`
  query LostFoundItemList($filter: LostFoundItemFilter, $params: PaginationAndSorting) {
    lostFoundItemList(filter: $filter, params: $params) {
      code
      total
      data { ${ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query LostFoundItemGet($id: ID) {${RESULT("lostFoundItemGet(id: $id)")}
  }
`;

const BY_CODE_QUERY = gql`
  query LostFoundItemByCode($params: LostFoundItemByCodeInput) {${RESULT("lostFoundItemByCode(params: $params)")}
  }
`;

const CREATE_MUTATION = gql`
  mutation LostFoundItemCreate($params: LostFoundItemInput) {${RESULT("lostFoundItemCreate(params: $params)")}
  }
`;

const UPDATE_MUTATION = gql`
  mutation LostFoundItemUpdate($where: LostFoundItemWhereInput, $params: LostFoundItemInput) {${RESULT("lostFoundItemUpdate(where: $where, params: $params)")}
  }
`;

const REMOVE_MUTATION = gql`
  mutation LostFoundItemRemove($where: LostFoundItemWhereInput) {
    lostFoundItemRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const MOVE_MUTATION = gql`
  mutation LostFoundItemMove($params: LostFoundItemMoveInput) {${RESULT("lostFoundItemMove(params: $params)")}
  }
`;

const ADD_PHOTO_MUTATION = gql`
  mutation LostFoundItemAddPhoto($params: LostFoundItemAddPhotoInput) {${RESULT("lostFoundItemAddPhoto(params: $params)")}
  }
`;

const REMOVE_PHOTO_MUTATION = gql`
  mutation LostFoundItemRemovePhoto($params: LostFoundItemRemovePhotoInput) {${RESULT("lostFoundItemRemovePhoto(params: $params)")}
  }
`;

export interface LostFoundPerson {
  kind?: "guest" | "person" | string | null;
  id?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  removed?: boolean | null;
}

export interface LostFoundPhoto {
  id: string;
  uploadedFileId?: string | null;
  uploadedFile?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  addedBy?: string | null;
  caption?: string | null;
}

export interface LostFoundItem {
  id?: string;
  accountId?: string;
  eventId?: string;
  eventTimeSlotId?: string | null;
  sequenceNo?: number | null;
  code?: string;
  source?: string;
  /** Made while the event was in test mode — hidden once it goes live. */
  isTest?: boolean | null;
  category?: string;
  isValuable?: boolean;
  publicDescription?: string | null;
  description?: string | null;
  foundAt?: string | null;
  foundWhere?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  locationAccuracyM?: number | null;
  foundServicePointId?: string | null;
  foundMapElementId?: string | null;
  finder?: LostFoundPerson | null;
  foundByLabel?: string | null;
  itemState?: LostFoundItemState | string;
  receivedAt?: string | null;
  receivedByLabel?: string | null;
  heldServicePointId?: string | null;
  storedWhere?: string | null;
  ownerGuestId?: string | null;
  owner?: LostFoundPerson | null;
  ownerQrTagId?: string | null;
  keepUntil?: string | null;
  returnedAt?: string | null;
  returnedByLabel?: string | null;
  returnedTo?: LostFoundPerson | null;
  returnedToReportId?: string | null;
  returnedServicePointId?: string | null;
  verificationKind?: string | null;
  verificationNote?: string | null;
  disposalKind?: string | null;
  disposedAt?: string | null;
  disposedByLabel?: string | null;
  photos?: LostFoundPhoto[] | null;
  rights?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface LostFoundItemInput {
  /** Create-only. */
  eventId?: string;
  category?: string;
  isValuable?: boolean | null;
  publicDescription?: string | null;
  description?: string | null;
  foundAt?: string | null;
  foundWhere?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  locationAccuracyM?: number | null;
  foundServicePointId?: string | null;
  foundMapElementId?: string | null;
  foundByLabel?: string | null;
  heldServicePointId?: string | null;
  storedWhere?: string | null;
  ownerGuestId?: string | null;
  ownerQrTagId?: string | null;
  eventTimeSlotId?: string | null;
}

export interface LostFoundItemFilter {
  id?: string[];
  eventId?: string[];
  eventTimeSlotId?: string[];
  itemState?: string[];
  category?: string[];
  source?: string[];
  isValuable?: boolean | null;
  searchKeyword?: string;
}

/** Someone typed in at the desk (not a guest): a name, and an email or a phone. */
export interface LostFoundContactInput {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface LostFoundItemMoveInput {
  id: string;
  toState: LostFoundItemState | string;
  storedWhere?: string | null;
  heldServicePointId?: string | null;
  publicDescription?: string | null;
  returnedToGuestId?: string | null;
  returnedToReportId?: string | null;
  returnedToContact?: LostFoundContactInput | null;
  verificationKind?: string | null;
  verificationNote?: string | null;
  servicePointId?: string | null;
  disposalKind?: string | null;
}

export class LostFoundItemService {
  static async list(filter?: LostFoundItemFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  /** The item a finder's QR or a bag label names, on this event — or a refusal at `code`. */
  static async byCode(eventId: string, code: string): Promise<OpResult> {
    return ApiService.executeQuery(BY_CODE_QUERY, { params: { eventId, code } });
  }

  static async create(params: LostFoundItemInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(id: string, params: LostFoundItemInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  static async move(params: LostFoundItemMoveInput): Promise<OpResult> {
    return ApiService.executeMutation(MOVE_MUTATION, { params });
  }

  /** A photo as base64 (scaled down in the browser first — `shrinkPhotoForUpload`). */
  static async addPhoto(id: string, dataBase64: string, caption?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(ADD_PHOTO_MUTATION, { params: { id, dataBase64, caption: caption || null } });
  }

  static async removePhoto(photoId: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_PHOTO_MUTATION, { params: { id: photoId } });
  }
}
